import { MESES } from "./pygAnaliticoBuild";
import { ARAMIS_DATA_KEY, buildAramisProjection } from "./pygAramis";

export const yearDataKey = (year) => `y${year}`;

/**
 * Series mensual y acumulada para N años (claves y2025, y2026, …).
 * Si hay proyección ARAMIS del año en curso, añade la serie fantasma.
 *
 * Acumulado ARAMIS: YTD real hasta el mes anterior al actual + proyección hacia adelante
 * (lectura de cierre). En el mensual, los meses cerrados usan backtest (error del modelo).
 *
 * @param {object} serieByYear  Serie de venta total (contable + pipeline) para las líneas del gráfico.
 * @param {object} [aramisSerieByYear]  Serie de venta SOLO CONTABLE para que ARAMIS calcule la
 *   estacionalidad y el YTD correctamente, sin que el pipeline futuro contamine la proyección.
 *   Si se omite, se usa serieByYear (comportamiento anterior).
 */
export const buildYearCompareChartData = (
  serieByYear,
  compareYears,
  { enableAramis = true, asOfMonth, targetYear, aramisSerieByYear } = {},
) => {
  const years = Array.isArray(compareYears) ? compareYears : [];
  if (!years.length) return { chartData: [], acumChartData: [], aramis: null };

  const now = new Date();
  const aramisYear = targetYear ?? now.getFullYear();
  const aramisMonth = asOfMonth ?? now.getMonth() + 1;

  // Usa la serie contable pura para ARAMIS si está disponible, evitando que el pipeline
  // futuro distorsione los pesos de estacionalidad y el YTD de cierre.
  const serieParaAramis = aramisSerieByYear ?? serieByYear;

  const aramis =
    enableAramis && years.includes(aramisYear)
      ? buildAramisProjection(serieParaAramis, {
          targetYear: aramisYear,
          asOfMonth: aramisMonth,
          compareYears: years,
        })
      : null;

  const acums = Object.fromEntries(years.map((y) => [y, 0]));
  let aramisAcum = 0;
  let actualClosedAcum = 0;
  const monthly = [];
  const acumulado = [];

  MESES.forEach((mes, i) => {
    const mesNum = parseInt(mes, 10);
    const row = { mes: `M${i + 1}` };
    years.forEach((year) => {
      const v = serieByYear?.[year]?.mes?.[mesNum] ?? 0;
      row[yearDataKey(year)] = v;
      acums[year] += v;
    });

    if (aramis) {
      const aramisVal = aramis.monthly[mesNum];
      if (aramisVal != null) {
        if (mesNum >= aramis.asOfMonth) {
          // Forward: proyección contable + pipeline conocido del mes
          const totalM = serieByYear?.[aramis.targetYear]?.mes?.[mesNum] ?? 0;
          const contableM =
            serieParaAramis?.[aramis.targetYear]?.mes?.[mesNum] ?? 0;
          const pipelineM = Math.max(0, totalM - contableM);
          row[ARAMIS_DATA_KEY] = aramisVal + pipelineM;
        } else {
          // Backtest: solo la proyección contable (para ver el error vs real)
          row[ARAMIS_DATA_KEY] = aramisVal;
        }
      } else {
        row[ARAMIS_DATA_KEY] = null;
      }
    }
    monthly.push(row);

    const acRow = { mes: `M${i + 1}` };
    years.forEach((year) => {
      acRow[yearDataKey(year)] = acums[year];
    });

    if (aramis) {
      // Para el acum de meses cerrados, usamos la serie contable pura (sin pipeline)
      // para que el YTD refleje lo realmente facturado.
      const actualM = serieParaAramis?.[aramis.targetYear]?.mes?.[mesNum] ?? 0;
      if (mesNum < aramis.asOfMonth) {
        // Meses cerrados: acum ARAMIS sigue el real contable (el error se ve en el mensual)
        actualClosedAcum += actualM;
        aramisAcum = actualClosedAcum;
      } else {
        // Mes actual y siguientes: YTD contable cerrado
        //   + proyección contable ARAMIS para esos meses
        //   + pipeline ya conocido (pedidos/proyecciones de serieByYear)
        //   evitando doble contar la parte contable (serieParaAramis) vs total (serieByYear)
        let projectedSum = 0;
        for (let m = aramis.asOfMonth; m <= mesNum; m += 1) {
          const aramisContable = aramis.monthly[m] ?? 0;
          const totalM = serieByYear?.[aramis.targetYear]?.mes?.[m] ?? 0;
          const contableM = serieParaAramis?.[aramis.targetYear]?.mes?.[m] ?? 0;
          const pipelineM = totalM - contableM; // pipeline conocido (pedidos + proyecciones)
          projectedSum += aramisContable + Math.max(0, pipelineM);
        }
        aramisAcum = actualClosedAcum + projectedSum;
      }
      acRow[ARAMIS_DATA_KEY] = aramisAcum;
    }

    acumulado.push(acRow);
  });

  return { chartData: monthly, acumChartData: acumulado, aramis };
};
